import { Injectable } from '@angular/core';
import { DocumentTypesList } from '@app/documents/models';
import { ProductNames, TableColumn, TenantNames } from '@mynexus/mynexus-ui-lib';
import { ServiceLineStatuses } from '../models/service-lines-status.enum';
import { SharedDocumentsService } from '@app/shared/services/shared-documents.service';
import { HttpClient } from '@angular/common/http';
import { ShellService } from '@mynexus/mfe-core';
import { DocumentLetterType } from '@app/core/model/document-letter-type.model';
import { Observable } from 'rxjs';
import { ServiceRequestTypes } from '@app/products/pac/models/service-request-types.enum';
import { ICoreAuthorizationDTO, IServicingProviderDTO } from '@app/core/model';

@Injectable({
  providedIn: 'root',
})
export class DocumentsUploadLetterPACService extends SharedDocumentsService {

  constructor(
    http: HttpClient,
    shellService: ShellService,
  ) {
    super(http, shellService, ProductNames.PACIM);
  }

  getLetterTypes(tenantId: string, authorizationByProductId: string): Observable<DocumentLetterType[]> {
    return this.http.get<DocumentLetterType[]>(`${this.getBaseUrl(tenantId, authorizationByProductId)}/GetUploadLetterTypes`);
  }

  getAuthorizationProvidersSummary(tenantId: string, authorizationByProductId: string): Observable<object> {
    const url = `${this.API_URL}/tenants/${tenantId}/authorization/${authorizationByProductId}/providers/summary`;
    return this.http.get<object>(url);
  }

  isSingleLineSelection(letterType: string): boolean {
    return letterType === DocumentTypesList.Dismissal ? false : true;
  }

  getLineTableConfig(): TableColumn[] {
    return [
      new TableColumn('Service No', 'serviceNumber', '10rem'),
      new TableColumn('Date Time Created', 'createdDate', '9rem'),
      new TableColumn('Date Time Request Received', 'requestReceivedDate', '9rem'),
      new TableColumn('Urgency', 'urgency', '9rem'),
      new TableColumn('Requested Days', 'daysRequested', '9rem'),
      new TableColumn('Approved Days', 'daysApproved', '8rem'),
      new TableColumn('Effective From', 'effectiveStartDate', '8rem'),
      new TableColumn('Effective Through', 'effectiveEndDate', '8rem'),
      new TableColumn('Status', 'serviceStatus', '8rem'),
      // The Decision column name was changed to Processed under CR-237303. The difference between column name and DTO property name IS intentional
      new TableColumn('Processed Date', 'decisionDate', '8rem'),
      new TableColumn('Date Urgency Change Downgr.', 'urgencyChangeDate', '8rem'),
      new TableColumn('Date Extension Taken', 'extensionTakenDate', '8rem'),
      new TableColumn('Status Reason', 'serviceStatusReason', '8rem'),
    ];
  }

  mapLineTableData(letterType: string, lineDto: object[], tenantName: TenantNames, authorization: ICoreAuthorizationDTO | null, servicingProvider: IServicingProviderDTO | null, authProviderPgpId: string | null) {
    const isBcbsriOONAuthorization = (tenantName === TenantNames.BCBSRI && servicingProvider !== null) ? this.isBcbsriOONAuthorization(authorization, servicingProvider, authProviderPgpId) : false;
    return lineDto.filter((x) => {
      const status = x['serviceStatus'];
      const serviceRequestType = x['serviceRequestType'];
      const urgencyChange = x['urgencyChange'];
      const urgencyChangeDate = x['urgencyChangeDate'];
      const extension = x['extension'];
      const allowedTenantsForReopen = [TenantNames.BCBSRI, TenantNames.NextBlue, TenantNames.WellmarkAdvantage, TenantNames.WyoBlueAdvantage];
      
      switch (letterType) {
        case DocumentTypesList.ApprovalLetter: return status === ServiceLineStatuses.Approved && !isBcbsriOONAuthorization && (tenantName === TenantNames.BCBSRI ? serviceRequestType !== ServiceRequestTypes.Reopen : true);
        case DocumentTypesList.IDNLetter: return status === ServiceLineStatuses.Denied;
        case DocumentTypesList.DismissalLetter: return status === ServiceLineStatuses.Dismissed;
        case DocumentTypesList.ExpeditedGrievanceLetter: return (urgencyChangeDate != null && urgencyChange === 'Downgrade') || !!extension;
        case DocumentTypesList.ReOpenedApprovalLetter: return allowedTenantsForReopen.includes(tenantName) && status === ServiceLineStatuses.Approved && serviceRequestType === ServiceRequestTypes.Reopen;
        case DocumentTypesList.OONApprovalLeter: return tenantName === TenantNames.BCBSRI && status === ServiceLineStatuses.Approved && serviceRequestType !== ServiceRequestTypes.Reopen && isBcbsriOONAuthorization;
        default: return false;
       }
    }).map(x => {
      const lineId = x['serviceLineId'];

      return {...x , lineId: lineId };
    });
  }

  isBcbsriOONAuthorization(authorization: ICoreAuthorizationDTO, servicingProvider: IServicingProviderDTO, authProviderPgpId: string): boolean{
    const isAuthOON = authorization?.streamData['outOfNetworkOverride'] ?? false; 
    const isSFOutOfMembersNetwork = servicingProvider?.providerAccountDTO?.outOfMembersNetwork ?? false;
    const isSFOutOfHpNetwork = (servicingProvider.providerPgpDTOList.filter(x=>x.facilityServiceType === authorization.streamData['facilityType'] && x.providerPgpId === authProviderPgpId)?.length>0 && 
                                servicingProvider.providerPgpDTOList.find(x=>x.facilityServiceType === authorization.streamData['facilityType'] && x.providerPgpId === authProviderPgpId)?.participationStatus) ?? false;
    const isSFOON = (isSFOutOfMembersNetwork || isSFOutOfHpNetwork);
    return isAuthOON && isSFOON;     
  }

  getNoLineMessage(documentType: string, tenantName: TenantNames): string {
    if(tenantName === TenantNames.BCBSRI && (documentType===DocumentTypesList.ApprovalLetter || documentType===DocumentTypesList.OONApprovalLeter))
    {
      return 'NOTE: The letter cannot be generated as authorization has no approved lines to select or more required criteria are not met.';
    }
    return '';
  }
}
